import type { TextareaHTMLAttributes } from 'react';

import { getInputClassName, isLabelFloating } from 'components/InputUi';

import FloatingLabel from './FloatingLabel';
import InputInfo from './InputInfo';

export interface Props extends TextareaHTMLAttributes<HTMLTextAreaElement> {
	errorMessage?: string | string[];
	helpText?: string;
	hiddenLabel?: boolean;
	id: string;
	invalid?: boolean;
	label: string;
	rows?: number;
	valid?: boolean;
}

export default function Textarea({
	className,
	defaultValue,
	disabled,
	errorMessage,
	helpText,
	hiddenLabel = false,
	id,
	invalid = false,
	label,
	readOnly,
	required,
	rows = 3,
	valid = false,
	value,
	...attrs
}: Props) {
	const errorMessageId = `${id}-error`;
	const helpTextId = `${id}-help`;

	const isEditable = !disabled && !readOnly;

	return (
		<div className={className}>
			<div className="relative">
				<textarea
					{...attrs}
					className={getInputClassName({
						disabled: !isEditable,
						extra: 'py-4',
						invalid,
						valid,
					})}
					aria-describedby={[errorMessageId, helpTextId].join(' ')}
					// Support for aria-errormessage isn't good enough as of autumn 2024
					// so stick with aria-describedby for now.
					// aria-errormessage={errorMessageId}
					aria-invalid={invalid}
					defaultValue={defaultValue}
					disabled={disabled}
					id={id}
					readOnly={readOnly}
					required={required}
					rows={rows}
					value={value}
				/>
				<FloatingLabel
					disabled={!isEditable}
					htmlFor={id}
					invalid={invalid}
					isFloating={isLabelFloating(value, defaultValue)}
					isHidden={hiddenLabel}
					required={required}
					valid={valid}
				>
					{label}
				</FloatingLabel>
			</div>
			<InputInfo
				errorMessage={errorMessage}
				errorMessageId={errorMessageId}
				helpText={helpText}
				helpTextId={helpTextId}
				invalid={invalid}
			/>
		</div>
	);
}
Textarea.displayName = 'FormUI_Textarea';
